import { supabase, DEMO_MODE } from './supabase';
import type {
  Account,
  Trade,
  Strategy,
  PropExpense,
  PropPayout,
  BudgetSettings,
  Setup,
  DailyNote,
  CalendarPrefs,
} from '../types';
import type { CoachAssignment } from './coach';
import type { EmotionLog } from './emotions';
import { DEFAULT_CALENDAR_PREFS } from '../types';

// לוג שגיאות Supabase בצורה ברורה
function logErr(fn: string, error: { message: string; code?: string } | null) {
  if (error) console.error(`[DB] ${fn}:`, error.message, error.code ?? '');
}

// ─── LOAD ────────────────────────────────────────────────────
export async function loadUserData(
  userId: string,
  opts: { skipProvision?: boolean } = {},
): Promise<{
  accounts: Account[];
  strategies: Strategy[];
  trades: Trade[];
  expenses: PropExpense[];
  payouts: PropPayout[];
  setups: Setup[];
  dailyGoalTarget: number;
  dailyMaxLoss: number;
  breakEvenRange: number;
  subscriptionStatus: 'active' | 'trialing' | 'expired' | null;
  trialEndsAt: string | null;
  budgetSettings: BudgetSettings | null;
  calendarPrefs: CalendarPrefs;
  timezone: string;
  timezoneFromDb: boolean;
}> {
  if (DEMO_MODE || !supabase)
    return {
      accounts: [],
      strategies: [],
      trades: [],
      expenses: [],
      payouts: [],
      setups: [],
      dailyGoalTarget: 0,
      dailyMaxLoss: 0,
      breakEvenRange: 0,
      subscriptionStatus: null,
      trialEndsAt: null,
      budgetSettings: null,
      calendarPrefs: DEFAULT_CALENDAR_PREFS,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneFromDb: false,
    };

  // צור profile אם לא קיים (fallback על המקרה שה-trigger לא רץ) — מתחיל בתקופת ניסיון.
  // ignoreDuplicates ⇒ INSERT ... ON CONFLICT DO NOTHING, לא דורס משתמשים קיימים.
  // skipProvision: mentor viewing a student — no write access to the student's profile.
  if (!opts.skipProvision) {
    const { error: pe } = await supabase.from('profiles').upsert(
      {
        id: userId,
        subscription_status: 'trialing',
        trial_ends_at: new Date(Date.now() + 14 * 86_400_000).toISOString(),
      },
      { onConflict: 'id', ignoreDuplicates: true },
    );
    if (pe) logErr('profiles.upsert', pe);
  }

  const [profileRes, accRes, stratRes, tradeRes, expRes, payRes, setupRes] = await Promise.all([
    supabase
      .from('profiles')
      .select(
        'daily_goal_target, daily_max_loss, break_even_range, subscription_status, trial_ends_at, budget_settings, calendar_prefs, timezone',
      )
      .eq('id', userId)
      .single(),
    supabase.from('accounts').select('*').eq('user_id', userId).order('created_at'),
    supabase
      .from('strategies')
      .select('*, strategy_fields(*)')
      .eq('user_id', userId)
      .order('created_at'),
    supabase
      .from('trades')
      .select('*, trade_media(*)')
      .eq('user_id', userId)
      .order('trade_date', { ascending: false }),
    supabase
      .from('prop_expenses')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false }),
    supabase
      .from('prop_payouts')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false }),
    supabase.from('setups').select('*').eq('user_id', userId).order('created_at'),
  ]);

  logErr('profiles.select', profileRes.error);
  logErr('accounts.select', accRes.error);
  logErr('strategies.select', stratRes.error);
  logErr('trades.select', tradeRes.error);
  logErr('prop_expenses.select', expRes.error);
  logErr('prop_payouts.select', payRes.error);
  logErr('setups.select', setupRes.error);

  // If the accounts query failed (e.g. expired session, RLS), throw so the caller's
  // .catch() keeps the localStorage cache instead of treating it as a new user.
  if (accRes.error) throw new Error(`accounts fetch failed: ${accRes.error.message}`);

  const dailyGoalTarget = profileRes.data?.daily_goal_target ?? 0;
  const dailyMaxLoss = profileRes.data?.daily_max_loss ?? 0;
  const breakEvenRange = profileRes.data?.break_even_range ?? 0;
  const subscriptionStatus = (profileRes.data?.subscription_status ?? 'trialing') as
    | 'active'
    | 'trialing'
    | 'expired';
  const trialEndsAt = (profileRes.data?.trial_ends_at ?? null) as string | null;
  const budgetSettings = (profileRes.data?.budget_settings ?? null) as BudgetSettings | null;
  const calendarPrefs = {
    ...DEFAULT_CALENDAR_PREFS,
    ...(profileRes.data?.calendar_prefs ?? {}),
  } as CalendarPrefs;
  const rawTimezone = (profileRes.data?.timezone ?? null) as string | null;
  const timezone = rawTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneFromDb = rawTimezone !== null;

  const accounts: Account[] = (accRes.data ?? []).map((r) => ({
    id: r.id,
    user_id: r.user_id,
    name: r.name,
    account_type: r.account_type,
    broker: r.broker,
    market_type: r.market_type ?? 'futures',
    initial_balance: r.initial_balance ?? 0,
    currency: r.currency ?? 'USD',
    is_active: r.is_active ?? true,
    // Prop Firm fields
    prop_phase: r.prop_phase ?? undefined,
    prop_drawdown_type: r.prop_drawdown_type ?? undefined,
    prop_max_drawdown: r.prop_max_drawdown ?? undefined,
    prop_daily_limit: r.prop_daily_limit ?? undefined,
    prop_profit_target: r.prop_profit_target ?? undefined,
    prop_min_days: r.prop_min_days ?? undefined,
    prop_max_days: r.prop_max_days ?? undefined,
    prop_start_date: r.prop_start_date ?? undefined,
    prop_consistency_pct: r.prop_consistency_pct ?? undefined,
    prop_challenge_consistency_pct: r.prop_challenge_consistency_pct ?? undefined,
    payout_split_pct: r.payout_split_pct ?? undefined,
    payout_cycle_days: r.payout_cycle_days ?? undefined,
    payout_buffer: r.payout_buffer ?? undefined,
    payout_min: r.payout_min ?? undefined,
    payout_max: r.payout_max ?? undefined,
  }));

  const strategies: Strategy[] = (stratRes.data ?? []).map((r) => ({
    id: r.id,
    user_id: r.user_id,
    name: r.name,
    description: r.description ?? '',
    color: r.color ?? '#635bff',
    is_active: r.is_active ?? true,
    fields: (r.strategy_fields ?? [])
      .sort((a: { sort_order: number }, b: { sort_order: number }) => a.sort_order - b.sort_order)
      .map(
        (f: {
          id: string;
          strategy_id: string;
          field_type: string;
          label: string;
          placeholder?: string;
          is_required: boolean;
          sort_order: number;
        }) => ({
          id: f.id,
          strategy_id: f.strategy_id,
          field_type: f.field_type as 'checkbox' | 'text' | 'select' | 'tag',
          label: f.label,
          placeholder: f.placeholder,
          is_required: f.is_required,
          sort_order: f.sort_order,
        }),
      ),
  }));

  const trades: Trade[] = (tradeRes.data ?? []).map((r) => ({
    id: r.id,
    user_id: r.user_id,
    account_id: r.account_id,
    strategy_id: r.strategy_id,
    symbol: r.symbol,
    direction: r.direction,
    trade_date: r.trade_date,
    pnl: r.pnl ?? 0,
    size: r.size,
    entry_time: r.entry_time ?? undefined,
    exit_time: r.exit_time ?? undefined,
    entry_price: r.entry_price ?? undefined,
    exit_price: r.exit_price ?? undefined,
    commission: r.commission ?? undefined,
    stop_loss_pts: r.stop_loss_pts,
    take_profit_pts: r.take_profit_pts,
    mfe_dollars: r.mfe_dollars ?? undefined,
    setup_id: r.setup_id ?? undefined,
    discipline_checked: r.discipline_checked ?? undefined,
    discipline_skipped: r.discipline_skipped ?? undefined,
    discipline_score: r.discipline_score ?? undefined,
    tags: r.tags ?? undefined,
    htf_pd_array: r.htf_pd_array,
    psychology: r.psychology,
    notes: r.notes,
    confirmations: r.confirmations ?? {},
    field_values: r.field_values ?? {},
    source: r.source ?? 'manual',
    broker_trade_id: r.broker_trade_id ?? undefined,
    media: (r.trade_media ?? []).map(
      (m: { id: string; trade_id: string; storage_path: string; label?: string }) => ({
        id: m.id,
        trade_id: m.trade_id,
        storage_path: m.storage_path,
        label: m.label,
      }),
    ),
  }));

  const expenses: PropExpense[] = (expRes.data ?? []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    user_id: r.user_id as string,
    account_id: (r.account_id as string | null) ?? undefined,
    prop_firm: r.prop_firm as string,
    account_size: (r.account_size as number) ?? 0,
    fee_type: r.fee_type as PropExpense['fee_type'],
    amount: (r.amount as number) ?? 0,
    date: r.date as string,
    notes: (r.notes as string | null) ?? undefined,
  }));

  const payouts: PropPayout[] = (payRes.data ?? []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    user_id: r.user_id as string,
    account_id: (r.account_id as string | null) ?? undefined,
    prop_firm: r.prop_firm as string,
    amount: (r.amount as number) ?? 0,
    date: r.date as string,
    notes: (r.notes as string | null) ?? undefined,
  }));

  const setups: Setup[] = (setupRes.data ?? []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    user_id: r.user_id as string,
    name: r.name as string,
    symbols: Array.isArray(r.symbols)
      ? (r.symbols as string[])
      : r.symbol && r.symbol !== ''
        ? [r.symbol as string]
        : [],
    emoji: (r.emoji as string) ?? '◆',
    hue: (r.hue as number) ?? 240,
    description: (r.description as string) ?? '',
    rules_entry: (r.rules_entry as string[]) ?? [],
    rules_exit: (r.rules_exit as string[]) ?? [],
    rules_risk: (r.rules_risk as string[]) ?? [],
    is_active: (r.is_active as boolean) ?? true,
    ai_tagged: (r.ai_tagged as boolean) ?? false,
    created_at: r.created_at as string,
    updated_at: r.updated_at as string,
  }));

  return {
    accounts,
    strategies,
    trades,
    expenses,
    payouts,
    setups,
    dailyGoalTarget,
    dailyMaxLoss,
    breakEvenRange,
    subscriptionStatus,
    trialEndsAt,
    budgetSettings,
    calendarPrefs,
    timezone,
    timezoneFromDb,
  };
}

// ─── PROP EXPENSES ───────────────────────────────────────────────────────────
export async function dbSaveExpense(expense: PropExpense, userId: string) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('prop_expenses').upsert({
    id: expense.id,
    user_id: userId,
    account_id: expense.account_id ?? null,
    prop_firm: expense.prop_firm,
    account_size: expense.account_size,
    fee_type: expense.fee_type,
    amount: expense.amount,
    date: expense.date,
    notes: expense.notes ?? null,
  });
  logErr('prop_expenses.upsert', error);
}

export async function dbDeleteExpense(id: string) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('prop_expenses').delete().eq('id', id);
  logErr('prop_expenses.delete', error);
}

// ─── PROP PAYOUTS ─────────────────────────────────────────────────────────────
export async function dbSavePayout(payout: PropPayout, userId: string) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('prop_payouts').upsert({
    id: payout.id,
    user_id: userId,
    account_id: payout.account_id ?? null,
    prop_firm: payout.prop_firm,
    amount: payout.amount,
    date: payout.date,
    notes: payout.notes ?? null,
  });
  logErr('prop_payouts.upsert', error);
}

export async function dbDeletePayout(id: string) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('prop_payouts').delete().eq('id', id);
  logErr('prop_payouts.delete', error);
}

// ─── PROFILE ─────────────────────────────────────────────────
export async function dbSaveProfile(
  userId: string,
  data: { dailyGoalTarget: number; dailyMaxLoss: number; budgetSettings?: BudgetSettings | null },
) {
  if (DEMO_MODE || !supabase) return;
  const payload: Record<string, unknown> = {
    id: userId,
    daily_goal_target: data.dailyGoalTarget,
    daily_max_loss: data.dailyMaxLoss,
  };
  if ('budgetSettings' in data) payload.budget_settings = data.budgetSettings ?? null;
  const { error } = await supabase.from('profiles').upsert(payload, { onConflict: 'id' });
  logErr('profiles.upsert', error);
}

export async function dbSaveBudgetSettings(userId: string, settings: BudgetSettings | null) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase
    .from('profiles')
    .upsert({ id: userId, budget_settings: settings }, { onConflict: 'id' });
  logErr('profiles.budget_settings.upsert', error);
}

export async function dbSaveTimezone(userId: string, timezone: string) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase
    .from('profiles')
    .upsert({ id: userId, timezone }, { onConflict: 'id' });
  logErr('profiles.upsert(timezone)', error);
}

export async function dbSaveBreakEvenRange(userId: string, value: number) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase
    .from('profiles')
    .upsert({ id: userId, break_even_range: value }, { onConflict: 'id' });
  logErr('profiles.upsert(break_even_range)', error);
}

export async function dbSaveCalendarPrefs(userId: string, prefs: CalendarPrefs) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase
    .from('profiles')
    .upsert({ id: userId, calendar_prefs: prefs }, { onConflict: 'id' });
  logErr('profiles.upsert(calendar_prefs)', error);
}

// Current Terms/Privacy version the user must have accepted. Bump when the
// policies change materially → ConsentGate re-prompts every existing user.
export const SIGNUP_CONSENT_VERSION = '2026-06';
// Device-local fast-path so the user who just consented isn't re-queried/re-prompted.
export const CONSENT_LOCAL_KEY = 'consent_local_version';

/**
 * Current consent state for the gate: which version accepted + marketing opt-in.
 * `ok` distinguishes a real read from a transient failure so the gate never nags
 * an already-consented user just because a query blipped.
 */
export async function dbLoadConsentStatus(
  userId: string,
): Promise<{ version: string | null; marketing: boolean; ok: boolean }> {
  if (DEMO_MODE || !supabase) return { version: null, marketing: false, ok: false };
  const { data, error } = await supabase
    .from('profiles')
    .select('consent_version, marketing_consent')
    .eq('id', userId)
    .single();
  logErr('profiles.select(consent_status)', error);
  if (error) return { version: null, marketing: false, ok: false };
  return {
    version: data?.consent_version ?? null,
    marketing: data?.marketing_consent === true,
    ok: true,
  };
}

/**
 * Record signup consent (Terms/Privacy acceptance + marketing opt-in).
 * Called once when a new user is seeded. Terms/Privacy acceptance is implied
 * by account creation (the signup checkbox gates it); marketing is an explicit,
 * separate opt-in (§30A) that defaults to false.
 */
export async function dbSaveSignupConsent(
  userId: string,
  opts: { version: string; marketing: boolean },
): Promise<boolean> {
  if (DEMO_MODE || !supabase) return false;
  const now = new Date().toISOString();
  const { error } = await supabase.from('profiles').upsert(
    {
      id: userId,
      consent_accepted_at: now,
      consent_version: opts.version,
      marketing_consent: opts.marketing,
      marketing_consent_at: now,
    },
    { onConflict: 'id' },
  );
  logErr('profiles.upsert(signup_consent)', error);
  return !error;
}

/** Persist UI language to the profile so server-side push can localize copy. */
export async function dbSaveLanguage(userId: string, language: 'he' | 'en') {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase
    .from('profiles')
    .upsert({ id: userId, language }, { onConflict: 'id' });
  logErr('profiles.upsert(language)', error);
}

export interface PushPrefs {
  recap?: boolean;
  targets?: boolean;
  risk?: boolean;
  intervention?: boolean; // intraday tilt alerts — opt-in (off unless true)
  quietStart?: number | null;
  quietEnd?: number | null;
}

export async function dbLoadPushPrefs(userId: string): Promise<PushPrefs> {
  if (DEMO_MODE || !supabase) return {};
  const { data, error } = await supabase
    .from('profiles')
    .select('push_prefs')
    .eq('id', userId)
    .single();
  logErr('profiles.select(push_prefs)', error);
  return (data?.push_prefs ?? {}) as PushPrefs;
}

export async function dbSavePushPrefs(userId: string, prefs: PushPrefs) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase
    .from('profiles')
    .upsert({ id: userId, push_prefs: prefs }, { onConflict: 'id' });
  logErr('profiles.upsert(push_prefs)', error);
}

/** Current marketing-email opt-in for the user (§30A). Defaults to false. */
export async function dbLoadMarketingConsent(userId: string): Promise<boolean> {
  if (DEMO_MODE || !supabase) return false;
  const { data, error } = await supabase
    .from('profiles')
    .select('marketing_consent')
    .eq('id', userId)
    .single();
  logErr('profiles.select(marketing_consent)', error);
  return data?.marketing_consent === true;
}

/** Update marketing-email opt-in. Lets the user withdraw/grant consent post-signup. */
export async function dbSaveMarketingConsent(userId: string, consent: boolean) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('profiles').upsert(
    {
      id: userId,
      marketing_consent: consent,
      marketing_consent_at: new Date().toISOString(),
    },
    { onConflict: 'id' },
  );
  logErr('profiles.upsert(marketing_consent)', error);
}

// ─── ACCOUNTS ────────────────────────────────────────────────
export async function dbSaveAccount(account: Account, userId: string) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('accounts').upsert({
    id: account.id,
    user_id: userId,
    name: account.name,
    account_type: account.account_type,
    broker: account.broker,
    market_type: account.market_type ?? 'futures',
    initial_balance: account.initial_balance,
    currency: account.currency,
    is_active: account.is_active,
    // Prop Firm fields
    prop_phase: account.prop_phase ?? null,
    prop_drawdown_type: account.prop_drawdown_type ?? null,
    prop_max_drawdown: account.prop_max_drawdown ?? null,
    prop_daily_limit: account.prop_daily_limit ?? null,
    prop_profit_target: account.prop_profit_target ?? null,
    prop_min_days: account.prop_min_days ?? null,
    prop_max_days: account.prop_max_days ?? null,
    prop_start_date: account.prop_start_date ?? null,
    prop_consistency_pct: account.prop_consistency_pct ?? null,
    prop_challenge_consistency_pct: account.prop_challenge_consistency_pct ?? null,
    payout_split_pct: account.payout_split_pct ?? null,
    payout_cycle_days: account.payout_cycle_days ?? null,
    payout_buffer: account.payout_buffer ?? null,
    payout_min: account.payout_min ?? null,
    payout_max: account.payout_max ?? null,
  });
  if (error) {
    logErr('accounts.upsert', error);
    throw new Error(error.message);
  }
}

export async function dbDeleteAccount(id: string) {
  if (DEMO_MODE || !supabase) return;
  // Remove the broker connection first — broker_connections.account_id has no FK
  // cascade, so a leftover row would block re-adding this Tradovate account later
  // (reconnect dedups by tradovate_account_id and would treat it as "already connected").
  const { error: connErr } = await supabase
    .from('broker_connections')
    .delete()
    .eq('account_id', id);
  logErr('broker_connections.delete', connErr);
  const { error } = await supabase.from('accounts').delete().eq('id', id);
  logErr('accounts.delete', error);
}

// ─── STRATEGIES ──────────────────────────────────────────────
export async function dbSaveStrategy(strategy: Strategy, userId: string) {
  if (DEMO_MODE || !supabase) return;

  const { error: se } = await supabase.from('strategies').upsert({
    id: strategy.id,
    user_id: userId,
    name: strategy.name,
    description: strategy.description ?? '',
    color: strategy.color,
    is_active: strategy.is_active,
  });
  logErr('strategies.upsert', se);
  if (se) return;

  // מחק שדות ישנים וכנס מחדש
  await supabase.from('strategy_fields').delete().eq('strategy_id', strategy.id);

  if (strategy.fields.length > 0) {
    const { error: fe } = await supabase.from('strategy_fields').insert(
      strategy.fields.map((f, i) => ({
        id: f.id,
        strategy_id: strategy.id,
        user_id: userId,
        field_type: f.field_type,
        label: f.label,
        placeholder: f.placeholder ?? null,
        is_required: f.is_required,
        sort_order: i + 1,
      })),
    );
    logErr('strategy_fields.insert', fe);
  }
}

export async function dbDeleteStrategy(id: string) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('strategies').delete().eq('id', id);
  logErr('strategies.delete', error);
}

// ─── TRADES ──────────────────────────────────────────────────
export async function dbSaveTrade(trade: Trade, userId: string) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('trades').upsert({
    id: trade.id,
    user_id: userId,
    account_id: trade.account_id,
    strategy_id: trade.strategy_id ?? null,
    symbol: trade.symbol,
    direction: trade.direction,
    trade_date: trade.trade_date,
    entry_time: trade.entry_time ?? null,
    exit_time: trade.exit_time ?? null,
    entry_price: trade.entry_price ?? null,
    exit_price: trade.exit_price ?? null,
    commission: trade.commission ?? null,
    pnl: trade.pnl,
    size: trade.size ?? null,
    stop_loss_pts: trade.stop_loss_pts ?? null,
    take_profit_pts: trade.take_profit_pts ?? null,
    mfe_dollars: trade.mfe_dollars ?? null,
    htf_pd_array: trade.htf_pd_array ?? null,
    psychology: trade.psychology ?? null,
    notes: trade.notes ?? null,
    confirmations: trade.confirmations ?? {},
    field_values: trade.field_values ?? {},
    source: trade.source ?? 'manual',
    status: 'complete',
    broker_trade_id: trade.broker_trade_id ?? null,
    setup_id: trade.setup_id ?? null,
    discipline_checked: trade.discipline_checked ?? [],
    discipline_skipped: trade.discipline_skipped ?? [],
    discipline_score: trade.discipline_score ?? null,
    tags: trade.tags ?? [],
  });
  logErr('trades.upsert', error);
}

export async function dbSaveTradeMfe(tradeId: string, mfeDollars: number, userId: string) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase
    .from('trades')
    .update({ mfe_dollars: mfeDollars })
    .eq('id', tradeId)
    .eq('user_id', userId);
  logErr('trades.update.mfe', error);
}

export async function dbDeleteTrade(id: string) {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('trades').delete().eq('id', id);
  logErr('trades.delete', error);
}

export async function dbUploadTradeMedia(
  tradeId: string,
  userId: string,
  files: File[],
): Promise<void> {
  if (DEMO_MODE || !supabase) return;
  for (const file of files) {
    const ext = file.name.split('.').pop() ?? 'jpg';
    const path = `${userId}/${tradeId}/${crypto.randomUUID()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from('trade-media')
      .upload(path, file, { cacheControl: '3600', upsert: false });
    if (upErr) {
      console.error('[DB] storage.upload failed:', upErr.message, upErr);
      continue;
    }
    const { error: dbErr } = await supabase.from('trade_media').insert({
      id: crypto.randomUUID(),
      trade_id: tradeId,
      user_id: userId,
      storage_path: path,
    });
    if (dbErr) {
      console.error('[DB] trade_media.insert failed:', dbErr.message, dbErr);
    }
  }
}

export async function dbGetTradeMediaUrls(mediaPaths: string[]): Promise<string[]> {
  if (DEMO_MODE || !supabase || mediaPaths.length === 0) return [];
  const urls = await Promise.all(
    mediaPaths.map(async (path) => {
      const { data } = await supabase!.storage.from('trade-media').createSignedUrl(path, 60 * 60); // שעה
      return data?.signedUrl ?? '';
    }),
  );
  return urls.filter(Boolean);
}

// ─── SEED ────────────────────────────────────────────────────
export async function dbSeedNewUser(userId: string, account: Account, strategies: Strategy[]) {
  if (DEMO_MODE || !supabase) return;
  await dbSaveAccount(account, userId);
  for (const s of strategies) await dbSaveStrategy(s, userId);
}

// ─── SETUPS ──────────────────────────────────────────────────────────────────

export async function dbLoadSetups(userId: string): Promise<Setup[]> {
  if (DEMO_MODE || !supabase) return [];
  const { data, error } = await supabase
    .from('setups')
    .select('*')
    .eq('user_id', userId)
    .order('created_at');
  logErr('setups.select', error);
  return (data ?? []).map((r) => ({
    id: r.id,
    user_id: r.user_id,
    name: r.name,
    symbols: Array.isArray(r.symbols)
      ? (r.symbols as string[])
      : r.symbol && r.symbol !== ''
        ? [r.symbol as string]
        : [],
    emoji: r.emoji ?? '◆',
    hue: r.hue ?? 240,
    description: r.description ?? '',
    rules_entry: r.rules_entry ?? [],
    rules_exit: r.rules_exit ?? [],
    rules_risk: r.rules_risk ?? [],
    is_active: r.is_active ?? true,
    ai_tagged: r.ai_tagged ?? false,
    created_at: r.created_at,
    updated_at: r.updated_at,
  }));
}

export async function dbSaveSetup(setup: Setup, userId: string): Promise<void> {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('setups').upsert({
    id: setup.id,
    user_id: userId,
    name: setup.name,
    symbols: setup.symbols,
    symbol: setup.symbols[0] ?? '', // keep symbol col populated for DB compat
    emoji: setup.emoji,
    hue: setup.hue,
    description: setup.description,
    rules_entry: setup.rules_entry,
    rules_exit: setup.rules_exit,
    rules_risk: setup.rules_risk,
    is_active: setup.is_active,
    ai_tagged: setup.ai_tagged,
    updated_at: new Date().toISOString(),
  });
  logErr('setups.upsert', error);
}

export async function dbDeleteSetup(id: string): Promise<void> {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('setups').delete().eq('id', id);
  logErr('setups.delete', error);
}

// ─── DAILY NOTES ─────────────────────────────────────────────
export async function dbLoadDailyNotes(userId: string): Promise<DailyNote[]> {
  if (DEMO_MODE || !supabase) return [];
  const { data, error } = await supabase
    .from('daily_notes')
    .select('*')
    .eq('user_id', userId)
    .order('note_date', { ascending: false });
  logErr('daily_notes.select', error);
  return (data ?? []) as DailyNote[];
}

export async function dbSaveDailyNote(
  userId: string,
  noteDate: string,
  note: string,
  disciplineRating: number | null,
): Promise<void> {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('daily_notes').upsert(
    {
      user_id: userId,
      note_date: noteDate,
      note,
      discipline_rating: disciplineRating,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,note_date' },
  );
  logErr('daily_notes.upsert', error);
}

// ─── EMOTION JOURNAL ─────────────────────────────────────────
export async function dbLoadEmotionLogs(userId: string, limit = 300): Promise<EmotionLog[]> {
  if (DEMO_MODE || !supabase) return [];
  const { data, error } = await supabase
    .from('emotion_logs')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  logErr('emotion_logs.select', error);
  return (data ?? []) as EmotionLog[];
}

export async function dbSaveEmotionLog(
  log: Omit<EmotionLog, 'created_at'> & { user_id: string },
): Promise<void> {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('emotion_logs').insert({
    id: log.id,
    user_id: log.user_id,
    log_date: log.log_date,
    emotion: log.emotion,
    intensity: log.intensity,
    trigger_note: log.trigger_note,
    note: log.note,
    trade_id: log.trade_id,
  });
  logErr('emotion_logs.insert', error);
}

export async function dbDeleteEmotionLog(id: string): Promise<void> {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('emotion_logs').delete().eq('id', id);
  logErr('emotion_logs.delete', error);
}

// ─── COACH ASSIGNMENTS ───────────────────────────────────────
export async function dbLoadCoachAssignments(userId: string): Promise<CoachAssignment[]> {
  if (DEMO_MODE || !supabase) return [];
  const { data, error } = await supabase
    .from('coach_assignments')
    .select('*')
    .eq('user_id', userId)
    .order('started_at', { ascending: false });
  logErr('coach_assignments.select', error);
  return (data ?? []) as CoachAssignment[];
}

export async function dbSaveCoachAssignment(a: CoachAssignment, userId: string): Promise<void> {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('coach_assignments').upsert({
    id: a.id,
    user_id: userId,
    template_id: a.template_id,
    started_at: a.started_at,
    target: a.target,
    param: a.param,
    status: a.status,
    completed_at: a.completed_at ?? null,
    final_compliance: a.final_compliance ?? null,
  });
  logErr('coach_assignments.upsert', error);
}

export async function dbDeleteCoachAssignment(id: string): Promise<void> {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('coach_assignments').delete().eq('id', id);
  logErr('coach_assignments.delete', error);
}

export async function dbSaveTradeDiscipline(
  tradeId: string,
  userId: string,
  discipline: {
    setup_id: string | null;
    discipline_checked: string[];
    discipline_skipped: string[];
    discipline_score: number | null;
  },
): Promise<void> {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase
    .from('trades')
    .update({
      setup_id: discipline.setup_id,
      discipline_checked: discipline.discipline_checked,
      discipline_skipped: discipline.discipline_skipped,
      discipline_score: discipline.discipline_score,
    })
    .eq('id', tradeId)
    .eq('user_id', userId);
  logErr('trades.update.discipline', error);
}

// ─── MENTOR SHARE (tokenized read-only journal links) ──────────
export interface JournalShare {
  id: string;
  token: string;
  scope: { accountIds?: string[]; hideDollar?: boolean };
  is_active: boolean;
  created_at: string;
}

const APP_ORIGIN = typeof window !== 'undefined' ? window.location.origin : 'https://traderyo.com';
export const getShareUrl = (token: string) => `${APP_ORIGIN}/share/${token}`;

export async function dbLoadShares(userId: string): Promise<JournalShare[]> {
  if (DEMO_MODE || !supabase) return [];
  const { data, error } = await supabase
    .from('journal_shares')
    .select('id, token, scope, is_active, created_at')
    .eq('user_id', userId)
    .eq('is_active', true)
    .order('created_at', { ascending: false });
  logErr('journal_shares.select', error);
  return (data ?? []) as JournalShare[];
}

export async function dbCreateShare(
  userId: string,
  scope: { accountIds?: string[]; hideDollar?: boolean } = {},
): Promise<JournalShare | null> {
  if (DEMO_MODE || !supabase) return null;
  const { data, error } = await supabase
    .from('journal_shares')
    .insert({ user_id: userId, scope })
    .select('id, token, scope, is_active, created_at')
    .single();
  logErr('journal_shares.insert', error);
  return (data ?? null) as JournalShare | null;
}

export async function dbRevokeShare(id: string): Promise<void> {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase
    .from('journal_shares')
    .update({ is_active: false, revoked_at: new Date().toISOString() })
    .eq('id', id);
  logErr('journal_shares.revoke', error);
}

// ─── NPS / TESTIMONIALS ────────────────────────────────────────
export interface Testimonial {
  comment: string;
  display_name: string | null;
}

export async function dbSaveFeedback(input: {
  userId: string;
  score: number;
  comment?: string;
  displayName?: string;
  canPublish?: boolean;
}): Promise<void> {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('feedback').insert({
    user_id: input.userId,
    score: input.score,
    comment: input.comment?.trim() || null,
    display_name: input.displayName?.trim() || null,
    can_publish: !!input.canPublish,
  });
  logErr('feedback.insert', error);
}

/** True if the user already submitted feedback within `days` (skip re-prompting). */
export async function dbHasRecentFeedback(userId: string, days = 90): Promise<boolean> {
  if (DEMO_MODE || !supabase) return false;
  const since = new Date(Date.now() - days * 86_400_000).toISOString();
  const { data, error } = await supabase
    .from('feedback')
    .select('id')
    .eq('user_id', userId)
    .gte('created_at', since)
    .limit(1);
  logErr('feedback.recent', error);
  return (data?.length ?? 0) > 0;
}

/** Approved, publishable testimonials for the landing page (anon-readable). */
export async function dbLoadPublicTestimonials(limit = 12): Promise<Testimonial[]> {
  if (DEMO_MODE || !supabase) return [];
  const { data, error } = await supabase
    .from('feedback')
    .select('comment, display_name')
    .eq('approved', true)
    .eq('can_publish', true)
    .not('comment', 'is', null)
    .order('created_at', { ascending: false })
    .limit(limit);
  logErr('feedback.public', error);
  return (data ?? []) as Testimonial[];
}

// ─── MENTOR ACCOUNTS (read-only "view as student") ─────────────
export interface MentorLink {
  id: string;
  student_id: string;
  student_name: string | null;
  student_email: string | null;
  mentor_email: string;
  mentor_id: string | null;
  status: string;
  created_at: string;
}

/** Load a student's data for read-only mentor viewing — no profile provisioning. */
export async function loadViewingData(studentId: string) {
  return loadUserData(studentId, { skipProvision: true });
}

/** Student invites a mentor by email. */
export async function dbInviteMentor(
  student: { id: string; name?: string | null; email?: string | null },
  mentorEmail: string,
): Promise<MentorLink | null> {
  if (DEMO_MODE || !supabase) return null;
  const { data, error } = await supabase
    .from('mentor_links')
    .insert({
      student_id: student.id,
      student_name: student.name ?? null,
      student_email: student.email ?? null,
      mentor_email: mentorEmail.trim().toLowerCase(),
      status: 'active',
    })
    .select('*')
    .single();
  logErr('mentor_links.insert', error);
  return (data ?? null) as MentorLink | null;
}

/** Mentors a student has invited. */
export async function dbListMentors(studentId: string): Promise<MentorLink[]> {
  if (DEMO_MODE || !supabase) return [];
  const { data, error } = await supabase
    .from('mentor_links')
    .select('*')
    .eq('student_id', studentId)
    .eq('status', 'active')
    .order('created_at', { ascending: false });
  logErr('mentor_links.listMentors', error);
  return (data ?? []) as MentorLink[];
}

export async function dbRevokeMentor(id: string): Promise<void> {
  if (DEMO_MODE || !supabase) return;
  const { error } = await supabase.from('mentor_links').delete().eq('id', id);
  logErr('mentor_links.revoke', error);
}

/** Students who granted ME (the current mentor) access. */
export async function dbListStudents(): Promise<MentorLink[]> {
  if (DEMO_MODE || !supabase) return [];
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('mentor_links')
    .select('*')
    .eq('mentor_id', user.id)
    .eq('status', 'active')
    .order('created_at', { ascending: false });
  logErr('mentor_links.listStudents', error);
  return (data ?? []) as MentorLink[];
}

/** Email the mentor a sign-in invite (server sends via Resend). Best-effort. */
export async function triggerMentorInviteEmail(
  mentorEmail: string,
  studentName: string,
  lang: 'he' | 'en',
): Promise<boolean> {
  if (DEMO_MODE || !supabase) return false;
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return false;
  try {
    const res = await fetch('/api/daily-digest?task=mentor-invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ mentorEmail, studentName, lang }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Bind pending links addressed to the caller's email (called on login). */
export async function dbBindMentorLinks(): Promise<number> {
  if (DEMO_MODE || !supabase) return 0;
  const { data, error } = await supabase.rpc('bind_mentor_links');
  logErr('mentor_links.bind', error);
  return (data as number) ?? 0;
}
